import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { normalizeNumberInput, sanitizeLeadingZeroInput } from "@/lib/utils";

const courierDefaults = "jne,sicepat,ide,sap,jnt,ninja,tiki,lion,anteraja,pos,ncs,rex,rpx,sentral,star,wahana,dse";

function emptyForm() {
  return {
    store_name: "",
    logo_url: "",
    primary_color: "#8A9A86",
    secondary_color: "#D5CABD",
    description: "",
    payment_timeout_rs: 24,
    payment_timeout_po: 72,
    shipping_api_key: "",
    shipping_origin_level: "subdistrict",
    shipping_origin_province_id: "",
    shipping_origin_province_name: "",
    shipping_origin_city_id: "",
    shipping_origin_city_name: "",
    shipping_origin_district_id: "",
    shipping_origin_district_name: "",
    shipping_origin_subdistrict_id: "",
    shipping_origin_subdistrict_name: "",
    shipping_couriers: courierDefaults,
  };
}

export default function AdminShipping() {
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [subdistrictOptions, setSubdistrictOptions] = useState([]);
  const [destinationQuery, setDestinationQuery] = useState("");
  const [destinationResults, setDestinationResults] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [weight, setWeight] = useState(1000);
  const [calculateCourier, setCalculateCourier] = useState("jne");
  const [calculation, setCalculation] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const { t } = useLanguage();

  const courierList = useMemo(() => {
    return (form.shipping_couriers || courierDefaults)
      .split(/[,:\s]+/)
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
  }, [form.shipping_couriers]);

  useEffect(() => {
    if (courierList.length && !courierList.includes(calculateCourier)) {
      setCalculateCourier(courierList[0]);
    }
  }, [courierList, calculateCourier]);

  const selectClass = "w-full mt-1 px-3 py-2 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:border-primary";

  const loadTenant = async () => {
    const r = await API.get("/tenant");
    const next = { ...emptyForm(), ...r.data };
    setForm(next);
    setCalculateCourier((next.shipping_couriers || courierDefaults).split(/[,:\s]+/).filter(Boolean)[0] || "jne");
    return next;
  };

  const loadProvinces = async (nextForm = form) => {
    const r = await API.get("/admin/shipping/rajaongkir/provinces");
    const provinces = r.data?.data || [];
    setProvinceOptions(provinces);

    if (nextForm.shipping_origin_province_id) {
      const provinceId = String(nextForm.shipping_origin_province_id);
      const selectedProvince = provinces.find((item) => String(item.id) === provinceId);
      if (selectedProvince) {
        await loadCities(provinceId, nextForm, selectedProvince.name);
      }
    }
  };

  const loadCities = async (provinceId, baseForm = form, provinceName = "") => {
    setCityOptions([]);
    setDistrictOptions([]);
    setSubdistrictOptions([]);
    if (!provinceId) return;
    const r = await API.get("/admin/shipping/rajaongkir/cities", { params: { province_id: provinceId } });
    const cities = r.data?.data || [];
    setCityOptions(cities);

    const selectedProvince = provinceName || provinceOptions.find((item) => String(item.id) === String(provinceId))?.name || "";
    setForm((prev) => ({
      ...prev,
      shipping_origin_province_id: String(provinceId),
      shipping_origin_province_name: selectedProvince,
      shipping_origin_city_id: baseForm.shipping_origin_city_id || "",
      shipping_origin_city_name: baseForm.shipping_origin_city_name || "",
      shipping_origin_district_id: baseForm.shipping_origin_district_id || "",
      shipping_origin_district_name: baseForm.shipping_origin_district_name || "",
      shipping_origin_subdistrict_id: baseForm.shipping_origin_subdistrict_id || "",
      shipping_origin_subdistrict_name: baseForm.shipping_origin_subdistrict_name || "",
    }));

    if (baseForm.shipping_origin_city_id) {
      const selectedCity = cities.find((item) => String(item.id) === String(baseForm.shipping_origin_city_id));
      if (selectedCity) {
        await loadDistricts(String(baseForm.shipping_origin_city_id), baseForm, selectedCity.name);
      }
    }
  };

  const loadDistricts = async (cityId, baseForm = form, cityName = "") => {
    setDistrictOptions([]);
    setSubdistrictOptions([]);
    if (!cityId) return;
    const r = await API.get("/admin/shipping/rajaongkir/districts", { params: { city_id: cityId } });
    const districts = r.data?.data || [];
    setDistrictOptions(districts);

    const selectedCity = cityName || cityOptions.find((item) => String(item.id) === String(cityId))?.name || "";
    setForm((prev) => ({
      ...prev,
      shipping_origin_city_id: String(cityId),
      shipping_origin_city_name: selectedCity,
      shipping_origin_district_id: baseForm.shipping_origin_district_id || "",
      shipping_origin_district_name: baseForm.shipping_origin_district_name || "",
      shipping_origin_subdistrict_id: baseForm.shipping_origin_subdistrict_id || "",
      shipping_origin_subdistrict_name: baseForm.shipping_origin_subdistrict_name || "",
    }));

    if (baseForm.shipping_origin_district_id) {
      const selectedDistrict = districts.find((item) => String(item.id) === String(baseForm.shipping_origin_district_id));
      if (selectedDistrict) {
        await loadSubdistricts(String(baseForm.shipping_origin_district_id), baseForm, selectedDistrict.name);
      }
    }
  };

  const loadSubdistricts = async (districtId, baseForm = form, districtName = "") => {
    setSubdistrictOptions([]);
    if (!districtId) return;
    const r = await API.get("/admin/shipping/rajaongkir/subdistricts", { params: { district_id: districtId } });
    const subdistricts = r.data?.data || [];
    setSubdistrictOptions(subdistricts);

    const selectedDistrict = districtName || districtOptions.find((item) => String(item.id) === String(districtId))?.name || "";
    setForm((prev) => ({
      ...prev,
      shipping_origin_district_id: String(districtId),
      shipping_origin_district_name: selectedDistrict,
      shipping_origin_subdistrict_id: baseForm.shipping_origin_subdistrict_id || "",
      shipping_origin_subdistrict_name: baseForm.shipping_origin_subdistrict_name || "",
    }));
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const next = await loadTenant();
        if (alive) {
          await loadProvinces(next);
        }
      } catch (err) {
        // Keep page usable even if key is not configured yet.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await API.put("/tenant", form);
      const next = { ...emptyForm(), ...r.data };
      setForm(next);
      setCalculateCourier((next.shipping_couriers || courierDefaults).split(/[,:\s]+/).filter(Boolean)[0] || "jne");
      toast.success("Shipping settings saved");
      await loadProvinces(next).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save shipping settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSearchDestination = async () => {
    if (!destinationQuery.trim()) {
      toast.error("Masukkan kata kunci tujuan");
      return;
    }
    try {
      const r = await API.get("/admin/shipping/rajaongkir/destinations", {
        params: { search: destinationQuery.trim(), limit: 10, offset: 0 },
      });
      setDestinationResults(r.data?.data || []);
      setSelectedDestination(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mencari tujuan");
    }
  };

  const handleCalculate = async () => {
    if (!form.shipping_origin_subdistrict_id) {
      toast.error("Pilih origin subdistrict dulu");
      return;
    }
    if (!selectedDestination?.id) {
      toast.error("Pilih tujuan dulu");
      return;
    }
    setCalculating(true);
    try {
      const r = await API.post("/admin/shipping/rajaongkir/calculate", {
        origin: form.shipping_origin_subdistrict_id,
        destination: String(selectedDestination.id),
        weight: Number(weight) || 1000,
        courier: calculateCourier || courierList[0] || "jne",
        price: "lowest",
      });
      setCalculation(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal hitung ongkir");
    } finally {
      setCalculating(false);
    }
  };

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  return (
    <AdminLayout title="Shipping Settings">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-white border border-stone-100 p-6 space-y-6">
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold">RajaOngkir Config</p>
            <p className="text-xs text-stone-500 mt-1">API key disimpan di backend. Kosongkan bila tidak ingin mengganti key lama.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">RajaOngkir API Key</Label>
            <Input
              type="password"
              value={form.shipping_api_key}
              onChange={(e) => updateForm({ shipping_api_key: e.target.value })}
              className="mt-1"
              placeholder="key dari RajaOngkir"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Origin Province</Label>
            <select
              className={selectClass}
              value={form.shipping_origin_province_id}
              onChange={async (e) => {
                const nextId = e.target.value;
                const nextName = provinceOptions.find((item) => String(item.id) === String(nextId))?.name || "";
                updateForm({
                  shipping_origin_province_id: nextId,
                  shipping_origin_province_name: nextName,
                  shipping_origin_city_id: "",
                  shipping_origin_city_name: "",
                  shipping_origin_district_id: "",
                  shipping_origin_district_name: "",
                  shipping_origin_subdistrict_id: "",
                  shipping_origin_subdistrict_name: "",
                });
                if (nextId) await loadCities(nextId, { shipping_origin_city_id: "", shipping_origin_district_id: "", shipping_origin_subdistrict_id: "" }, nextName);
              }}
            >
              <option value="">Pilih province</option>
              {provinceOptions.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">Origin City</Label>
              <select
                className={selectClass}
                value={form.shipping_origin_city_id}
                onChange={async (e) => {
                  const nextId = e.target.value;
                  const nextName = cityOptions.find((item) => String(item.id) === String(nextId))?.name || "";
                  updateForm({
                    shipping_origin_city_id: nextId,
                    shipping_origin_city_name: nextName,
                    shipping_origin_district_id: "",
                    shipping_origin_district_name: "",
                    shipping_origin_subdistrict_id: "",
                    shipping_origin_subdistrict_name: "",
                  });
                  if (nextId) await loadDistricts(nextId, { shipping_origin_district_id: "", shipping_origin_subdistrict_id: "" }, nextName);
                }}
              >
                <option value="">Pilih city</option>
                {cityOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">Origin District</Label>
              <select
                className={selectClass}
                value={form.shipping_origin_district_id}
                onChange={async (e) => {
                  const nextId = e.target.value;
                  const nextName = districtOptions.find((item) => String(item.id) === String(nextId))?.name || "";
                  updateForm({
                    shipping_origin_district_id: nextId,
                    shipping_origin_district_name: nextName,
                    shipping_origin_subdistrict_id: "",
                    shipping_origin_subdistrict_name: "",
                  });
                  if (nextId) await loadSubdistricts(nextId, { shipping_origin_subdistrict_id: "" }, nextName);
                }}
              >
                <option value="">Pilih district</option>
                {districtOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Origin Subdistrict</Label>
            <select
              className={selectClass}
              value={form.shipping_origin_subdistrict_id}
              onChange={(e) => {
                const nextId = e.target.value;
                const nextName = subdistrictOptions.find((item) => String(item.id) === String(nextId))?.name || "";
                updateForm({ shipping_origin_subdistrict_id: nextId, shipping_origin_subdistrict_name: nextName });
              }}
            >
              <option value="">Pilih subdistrict</option>
              {subdistrictOptions.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Default Couriers</Label>
            <Input
              value={form.shipping_couriers}
              onChange={(e) => updateForm({ shipping_couriers: e.target.value })}
              className="mt-1"
              placeholder="jne,sicepat,jnt"
            />
            <p className="text-xs text-stone-400 mt-1">Pisahkan dengan koma. Akan dinormalisasi ke format RajaOngkir saat kalkulasi.</p>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary text-primary-foreground px-6 py-3 text-sm uppercase tracking-wider hover:opacity-90 transition-colors disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Shipping Settings"}
          </button>
        </div>

        <div className="bg-white border border-stone-100 p-6 space-y-6">
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold">Shipping Cost Tester</p>
            <p className="text-xs text-stone-500 mt-1">Cari destination, lalu hitung ongkir dari origin subdistrict yang sudah disimpan.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Search Destination</Label>
            <div className="flex gap-2 mt-1">
              <Input value={destinationQuery} onChange={(e) => setDestinationQuery(e.target.value)} placeholder='contoh: Jakarta, Cilandak, 53355' />
              <button onClick={handleSearchDestination} className="px-4 py-2 rounded-md bg-stone-900 text-white text-sm">Search</button>
            </div>
          </div>

          {destinationResults.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto border border-stone-100 rounded-md p-3">
              {destinationResults.map((item) => {
                const active = selectedDestination?.id === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setSelectedDestination(item)}
                    className={`w-full text-left rounded-md border px-3 py-2 text-sm transition-colors ${active ? "border-primary bg-primary/5" : "border-stone-100 hover:border-stone-200"}`}
                  >
                    <div className="font-medium text-stone-900">{item.label || item.name || item.city_name || item.district_name || item.subdistrict_name}</div>
                    <div className="text-xs text-stone-500">
                      {item.province_name} · {item.city_name} · {item.district_name} · {item.subdistrict_name} · {item.zip_code}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">Weight (gram)</Label>
              <Input type="number" value={weight ?? ""} onChange={(e) => setWeight(sanitizeLeadingZeroInput(e.target.value))} onBlur={(e) => setWeight(normalizeNumberInput(e.target.value, 1000))} min={1} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">Courier</Label>
              <select className={selectClass} value={calculateCourier} onChange={(e) => setCalculateCourier(e.target.value)}>
                {courierList.map((item) => <option key={item} value={item}>{item.toUpperCase()}</option>)}
              </select>
            </div>
          </div>

          <button
            onClick={handleCalculate}
            disabled={calculating}
            className="bg-primary text-primary-foreground px-6 py-3 text-sm uppercase tracking-wider hover:opacity-90 transition-colors disabled:opacity-50"
          >
            {calculating ? "Calculating..." : "Calculate Cost"}
          </button>

          {selectedDestination && (
            <div className="text-xs text-stone-500 border border-stone-100 rounded-md p-3">
              Selected destination, {selectedDestination.label || selectedDestination.name}
            </div>
          )}

          {calculation?.data && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-stone-900">Result</p>
              <div className="space-y-2">
                {Array.isArray(calculation.data) && calculation.data.map((item, idx) => (
                  <div key={idx} className="rounded-md border border-stone-100 p-3 text-sm">
                    <div className="font-medium text-stone-900">{item.name || item.service || item.code}</div>
                    <div className="text-xs text-stone-500">{item.service} · {item.description}</div>
                    <div className="mt-1 text-sm text-stone-700">Rp{Number(item.cost || 0).toLocaleString("id-ID")} · ETD {item.etd || "-"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
